
import React, { useEffect, useState } from "react";
import { Button, Grid, CardContent, Card, Typography, Box } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { apiConstants, getGeneric, patchGeneric, postGeneric } from "../../common/constants";
import { useSnackbar } from "notistack";
import { LoadingButton } from "@mui/lab";
import { RHFTextField, RHFSelectField, FormProvider } from "../../common/hook-form";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";

// Yes/No is the only static dropdown — every other list is sourced from
// GET /api/order/serviceability/intel/form-options at form-mount time.
const YES_NO = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
];

export default function AddCourier(props) {
    const { setAddOpen, supported_shipment, setReload, isEditData } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Dropdown lookups fetched from /api/order/serviceability/intel/form-options on mount.
    // Source of truth = backend; no static fallback.
    const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
    const [chargeableWeightRuleOptions, setChargeableWeightRuleOptions] = useState([]);
    const [workingDaysOptions, setWorkingDaysOptions] = useState([]);
    const [ndrHandlingOptions, setNdrHandlingOptions] = useState([]);
    const [trackingTypeOptions, setTrackingTypeOptions] = useState([]);
    const [courierTypeOptions, setCourierTypeOptions] = useState([]);

    useEffect(() => {
        getGeneric(`order/serviceability/intel/form-options`)
            .then((res) => {
                const opts = res?.data?.responseObj || res?.data || {};
                const wrapWithNone = (list) =>
                    Array.isArray(list) && list.length > 0
                        ? [{ label: "(none)", value: "" }, ...list]
                        : [];
                setServiceTypeOptions(wrapWithNone(opts.serviceTypes));
                setChargeableWeightRuleOptions(Array.isArray(opts.chargeableWeightRule) ? opts.chargeableWeightRule : []);
                setWorkingDaysOptions(Array.isArray(opts.workingDays) ? opts.workingDays : []);
                setNdrHandlingOptions(Array.isArray(opts.ndrHandling) ? opts.ndrHandling : []);
                setTrackingTypeOptions(Array.isArray(opts.trackingType) ? opts.trackingType : []);
                setCourierTypeOptions(Array.isArray(opts.courierType) ? opts.courierType : []);
            })
            .catch((err) => {
                console.warn("form-options fetch failed; dropdowns will be empty", err);
            });
    }, []);

    const schema = yup.object().shape({
        name: yup.string().required("Full Name is required"),
        email: yup
            .string()
            .required("Email is required")
            .test("validEmails", "Please enter valid email addresses separated by commas", function(value) {
                if (!value) return false;

                const emails = value.split(',').map(email => email.trim());
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                return emails.every(email => emailRegex.test(email));
            }),
        contactNo: yup
            .string()
            .matches(/^[0-9]+$/, "Contact Number must be a valid number")
            .min(10, "Contact Number must be at least 10 digits")
            .max(15, "Contact Number cannot exceed 15 digits")
            .required("Contact Number is required"),
        prozoId: yup.string().required("Prozo Id is required"),
        parent: yup.string().required("Parent Name is required"),
        cpId: yup
            .number()
            .typeError("CP Id must be a number")
            .required("CP Id is required"),
        cpAccountCode: yup.string().required("CP Account Code is required"),
        type: yup.string().required("Courier Type is required"),
        dispatchMode: yup.string().required("Shipment Type is required"),

        // ── Courier attributes (final Excel) — all mandatory; serviceTypeSecondary optional ──
        serviceTypePrimary: yup.string().required("Primary Service Type is required"),
        chargeableWeightRule: yup.string().required("Chargeable Weight Rule is required"),
        multiPieceAllowed: yup.string().required("Multi-Piece Allowed is required"),
        fragileHandlingAvailable: yup.string().required("Fragile Handling is required"),
        specialPackagingRequired: yup.string().required("Special Packaging Required is required"),
        remarks: yup.string().required("Remarks is required"),
        metroServiceable: yup.string().required("Metro Serviceable is required"),
        nddEligible: yup.string().required("NDD Eligible is required"),
        sddEligible: yup.string().required("SDD Eligible is required"),
        pickupCutoffTime: yup.string().required("Pickup Cutoff Time is required"),
        pickupDays: yup.string().required("Pickup Days is required"),
        deliveryDays: yup.string().required("Delivery Days is required"),
        ndrHandling: yup.string().nullable(), // optional
        realTimeTracking: yup.string().required("Real-Time Tracking is required"),
        trackingType: yup.string().required("Tracking Type is required"),
        onboardedDate: yup.string().required("Onboarded Date is required"),
        minChargeableWeight: yup.number().typeError("Min Chargeable Weight must be a number").required("Min Chargeable Weight is required"),
        maxWeightSinglePiece: yup.number().typeError("Max Weight (single piece) must be a number").required("Max Weight (single piece) is required"),
        maxWeightPerShipment: yup.number().typeError("Max Weight (per shipment) must be a number").required("Max Weight (per shipment) is required"),
        volumetricDivisor: yup.number().typeError("Volumetric Divisor must be a number").required("Volumetric Divisor is required"),
        // Allocation weight band (optional, kg). Blank = open on that side. Min must not exceed max.
        effectiveMinWeight: yup.number().transform((v, o) => (o === '' ? undefined : v)).typeError("Effective Min Weight must be a number").min(0, "Effective Min Weight cannot be negative").notRequired(),
        effectiveMaxWeight: yup.number().transform((v, o) => (o === '' ? undefined : v)).typeError("Effective Max Weight must be a number").positive("Effective Max Weight must be > 0").notRequired()
            .test('min-le-max', 'Effective Max Weight must be >= Effective Min Weight', function (max) {
                const min = this.parent.effectiveMinWeight
                return max === undefined || min === undefined || min === null || Number(max) >= Number(min)
            }),
        maxLength: yup.number().typeError("Max Length must be a number").required("Max Length is required"),
        maxWidth: yup.number().typeError("Max Width must be a number").required("Max Width is required"),
        maxHeight: yup.number().typeError("Max Height must be a number").required("Max Height is required"),
        maxGirth: yup.number().typeError("Max Girth must be a number").required("Max Girth is required"),
        maxLwhSum: yup.number().typeError("Max L+W+H Sum must be a number").required("Max L+W+H Sum is required"),
        maxPiecesPerShipment: yup.number().typeError("Max Pieces / Shipment must be a number").required("Max Pieces / Shipment is required"),
        forwardSlaMetroDays: yup.number().typeError("Forward SLA — Metro must be a number").required("Forward SLA — Metro is required"),
        forwardSlaNonMetroDays: yup.number().typeError("Forward SLA — Non-Metro must be a number").required("Forward SLA — Non-Metro is required"),
        maxDeliveryAttempts: yup.number().typeError("Max Delivery Attempts must be a number").required("Max Delivery Attempts is required"),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            contactNo: '',
            prozoId: '',
            email: '',
            name: '',
            parent: '',
            type: '',
            dispatchMode: '',
            cpId: '',
            cpAccountCode: '',
            supported_shipment: '',
            isExternal: '',
            merchant: '',
            isBlended: '',
            isPrepaid: '',
            qcType: '',
            isSddNddEnabled: '',
            trackingOnly: '',
            prozoInternal: '',
            podIntegration: '',

            // ── Courier attributes — Weight & Dimensions (Excel) ──
            serviceTypePrimary: '',
            serviceTypeSecondary: '',
            minChargeableWeight: '',
            maxWeightSinglePiece: '',
            maxWeightPerShipment: '',
            volumetricDivisor: '',
            effectiveMinWeight: '',
            effectiveMaxWeight: '',
            maxLength: '',
            maxWidth: '',
            maxHeight: '',
            maxGirth: '',
            maxLwhSum: '',
            chargeableWeightRule: '',
            multiPieceAllowed: '',
            maxPiecesPerShipment: '',
            fragileHandlingAvailable: '',
            specialPackagingRequired: '',
            remarks: '',

            // ── Operational (Excel) ──
            metroServiceable: '',
            nddEligible: '',
            sddEligible: '',
            forwardSlaMetroDays: '',
            forwardSlaNonMetroDays: '',
            pickupCutoffTime: '',
            pickupDays: '',
            maxDeliveryAttempts: '',
            deliveryDays: '',
            ndrHandling: '',
            realTimeTracking: '',
            trackingType: '',
            onboardedDate: '',
        }
    });

    const { handleSubmit, reset, watch, setValue } = methods;

    useEffect(() => {
        if (isEditData) {
            setValue('contactNo', isEditData.contactNo)
            setValue('prozoId', isEditData.prozoId)
            setValue('email', isEditData.email)
            setValue('name', isEditData.name)
            setValue('parent', isEditData.parent)
            setValue('type', isEditData.type)
            setValue('cpId', isEditData.cpId)
            setValue('cpAccountCode', isEditData.cpAccountCode)
            setValue('dispatchMode', isEditData.dispatchMode)
            setValue('isSddNddEnabled', isEditData.isSddNddEnabled ? 'true' : 'false')
            setValue('qcType', isEditData.qcType ? isEditData.qcType : 'No')
            setValue('merchant', isEditData.merchantId || isEditData.merchant || '')
            setValue('isBlended', isEditData.isBlended === true ? 'true' : isEditData.isBlended === false ? 'false' : '')
            setValue('isExternal', isEditData.isExternal === true ? 'true' : isEditData.isExternal === false ? 'false' : '')
            setValue('isPrepaid', isEditData.isPrepaid === true ? 'true' : isEditData.isPrepaid === false ? 'false' : '')
            setValue('trackingOnly', isEditData.trackingOnly === true ? 'true' : isEditData.trackingOnly === false ? 'false' : '')
            setValue('prozoInternal', isEditData.prozoInternal === true ? 'true' : isEditData.prozoInternal === false ? 'false' : '')
            setValue('podIntegration', isEditData.podIntegration || '')

            // ── Serviceability Intel preload ──
            const boolStr = (v) => v === true ? 'true' : v === false ? 'false' : '';
            const valOr = (v) => (v === null || v === undefined) ? '' : v;

            setValue('serviceTypePrimary',       valOr(isEditData.serviceTypePrimary));
            setValue('serviceTypeSecondary',     valOr(isEditData.serviceTypeSecondary));
            setValue('minChargeableWeight',      valOr(isEditData.minChargeableWeight));
            setValue('maxWeightSinglePiece',     valOr(isEditData.maxWeightSinglePiece));
            setValue('maxWeightPerShipment',     valOr(isEditData.maxWeightPerShipment));
            setValue('volumetricDivisor',        valOr(isEditData.volumetricDivisor));
            setValue('effectiveMinWeight',       valOr(isEditData.effectiveMinWeight));
            setValue('effectiveMaxWeight',       valOr(isEditData.effectiveMaxWeight));
            setValue('maxLength',                valOr(isEditData.maxLength));
            setValue('maxWidth',                 valOr(isEditData.maxWidth));
            setValue('maxHeight',                valOr(isEditData.maxHeight));
            setValue('maxGirth',                 valOr(isEditData.maxGirth));
            setValue('maxLwhSum',                valOr(isEditData.maxLwhSum));
            setValue('chargeableWeightRule',     valOr(isEditData.chargeableWeightRule));
            setValue('multiPieceAllowed',        boolStr(isEditData.multiPieceAllowed));
            setValue('maxPiecesPerShipment',     valOr(isEditData.maxPiecesPerShipment));
            setValue('fragileHandlingAvailable', boolStr(isEditData.fragileHandlingAvailable));
            setValue('specialPackagingRequired', boolStr(isEditData.specialPackagingRequired));
            setValue('remarks',                  valOr(isEditData.remarks));

            setValue('metroServiceable',     boolStr(isEditData.metroServiceable));
            setValue('nddEligible',          boolStr(isEditData.nddEligible));
            setValue('sddEligible',          boolStr(isEditData.sddEligible));
            setValue('forwardSlaMetroDays',    valOr(isEditData.forwardSlaMetroDays));
            setValue('forwardSlaNonMetroDays', valOr(isEditData.forwardSlaNonMetroDays));

            setValue('pickupCutoffTime',         valOr(isEditData.pickupCutoffTime));
            setValue('pickupDays',               valOr(isEditData.pickupDays));
            setValue('maxDeliveryAttempts',      valOr(isEditData.maxDeliveryAttempts));
            setValue('deliveryDays',             valOr(isEditData.deliveryDays));
            setValue('ndrHandling',              valOr(isEditData.ndrHandling));
            setValue('realTimeTracking',         boolStr(isEditData.realTimeTracking));
            setValue('trackingType',             valOr(isEditData.trackingType));
            setValue('onboardedDate',            isEditData.onboardedDate ? String(isEditData.onboardedDate).substring(0, 10) : '');
        }else{
            setValue('isSddNddEnabled', 'false')
        }
    }, [isEditData])


    const onSubmit = async (data) => {
        setIsLoading(true);

        // Helpers: convert RHF string controls to real types only when set.
        const toBool = (v) => v === 'true' ? true : v === 'false' ? false : null;
        const toNum  = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);

        const intel = {
            // Weight & Dimensions
            serviceTypePrimary:       data.serviceTypePrimary || null,
            serviceTypeSecondary:     data.serviceTypeSecondary || null,
            minChargeableWeight:      toNum(data.minChargeableWeight),
            maxWeightSinglePiece:     toNum(data.maxWeightSinglePiece),
            maxWeightPerShipment:     toNum(data.maxWeightPerShipment),
            volumetricDivisor:        toNum(data.volumetricDivisor),
            effectiveMinWeight:       toNum(data.effectiveMinWeight),
            effectiveMaxWeight:       toNum(data.effectiveMaxWeight),
            maxLength:                toNum(data.maxLength),
            maxWidth:                 toNum(data.maxWidth),
            maxHeight:                toNum(data.maxHeight),
            maxGirth:                 toNum(data.maxGirth),
            maxLwhSum:                toNum(data.maxLwhSum),
            chargeableWeightRule:     data.chargeableWeightRule || null,
            multiPieceAllowed:        toBool(data.multiPieceAllowed),
            maxPiecesPerShipment:     toNum(data.maxPiecesPerShipment),
            fragileHandlingAvailable: toBool(data.fragileHandlingAvailable),
            specialPackagingRequired: toBool(data.specialPackagingRequired),
            remarks:                  data.remarks || null,
            // Operational
            metroServiceable:    toBool(data.metroServiceable),
            nddEligible:         toBool(data.nddEligible),
            sddEligible:         toBool(data.sddEligible),
            forwardSlaMetroDays:    toNum(data.forwardSlaMetroDays),
            forwardSlaNonMetroDays: toNum(data.forwardSlaNonMetroDays),
            pickupCutoffTime:         data.pickupCutoffTime || null,
            pickupDays:               data.pickupDays || null,
            maxDeliveryAttempts:      toNum(data.maxDeliveryAttempts),
            deliveryDays:             data.deliveryDays || null,
            ndrHandling:              data.ndrHandling || null,
            realTimeTracking:         toBool(data.realTimeTracking),
            trackingType:             data.trackingType || null,
            onboardedDate:            data.onboardedDate || null,
        };
        // Drop null intel fields so backend $set guards stay non-destructive.
        Object.keys(intel).forEach((k) => { if (intel[k] === null) delete intel[k]; });

        const payload = {
            ...data,
            ...intel,
            isExternal: data.isExternal ? data.isExternal : false,
            supported_shipment: supported_shipment,
            status: "0",
            is_deleted: "0",
            integration: "CP",
        };
        // "PoD via" is optional; not set = Manual on the backend.
        if (!payload.podIntegration) delete payload.podIntegration;
        if (isEditData) {
            payload.id = isEditData.id
        }
        if (payload.isExternal === 'true') {
            payload.merchantId = data.merchant;
            delete payload.merchant;
        } else {
            delete payload.merchant;
        }
        try {
            const response = isEditData ? await patchGeneric(`${apiConstants.UPDATE_COURIER_PARTNER}`, payload) : await postGeneric(`${apiConstants.CREATE_COURIER_PARTNER}`, payload);
            enqueueSnackbar(response?.data?.meta?.message || 'Success', { variant: 'success' });
        } catch (err) {
            console.log(err);
            const errorMessage = err?.response?.data?.message || err?.message || "Some error occurred!";
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setIsLoading(false);
            setAddOpen(false);
            setReload(reload => reload + 1);
        }
    };

    const isExternal = watch("isExternal")

    const SectionHeader = ({ title }) => (
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2.5, mb: 1.5, '&:first-of-type': { mt: 0 } }}>
                <Box sx={{ width: 4, height: 18, borderRadius: '2px', bgcolor: '#1565c0' }} />
                <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, fontWeight: 700, color: '#1e293b', letterSpacing: 0.2 }}>
                    {title}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: '#EEF2F6' }} />
            </Box>
        </Grid>
    );

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Card sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>

                        {/* Basic Information */}
                        <SectionHeader title="Basic Information" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="name" label="Full Name *" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField
                                name="email"
                                label="Email * (comma-separated for multiple)"
                                placeholder="email1@example.com, email2@example.com"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name='contactNo' label="Contact Number *" />
                        </Grid>

                        {/* Courier Partner Details */}
                        <SectionHeader title="Courier Partner Details" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="parent" label="Parent Name *" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="prozoId" label="Prozo Id *" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="cpId" label="CP Id *" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="cpAccountCode" label="CP Account Code *" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField
                                label="Shipment Type *"
                                name="dispatchMode"
                                width="100%"
                                options={[
                                    { label: "Surface", value: "SURFACE" },
                                    { label: "Air", value: "AIR" },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name='type' label="Courier Type *" width="100%" options={courierTypeOptions} />
                        </Grid>

                        {/* Configuration */}
                        <SectionHeader title="Configuration" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name='isBlended' label="Is Blended" width="100%" options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' }
                            ]} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            {/* Payment-mode flags are frozen on the update API (the backend ignores them on PATCH).
                                Read-only here so nobody "saves" a change that never lands. Change them via
                                Couriers → Bulk attribute upload; every change is audited. */}
                            <RHFSelectField name='isPrepaid' label="Is Prepaid (read-only)" width="100%" disabled options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' }
                            ]} />
                            <Typography variant='caption' sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                COD / Prepaid support can only be changed via Couriers → Bulk attribute upload. Check Courier Performance → Payment Mode Compliance after changing it.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name='isExternal' label="Is External" width="100%" options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' }
                            ]} />
                        </Grid>
                        {isExternal === 'true' && (
                            <Grid item xs={12} sm={6} md={4}>
                                <MerchantFilterCached name="merchant" label="Select Merchant" />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name='qcType' label="Qc Type" width="100%" options={[
                                { label: 'Yes', value: 'doorstep' },
                                { label: 'No', value: 'No' },
                            ]} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name='isSddNddEnabled' label="SDD NDD Enabled" width="100%" options={[
                                { label: 'True', value: 'true' },
                                { label: 'False', value: 'false' },
                            ]} />
                        </Grid>

                        {/* ─── Courier Attributes — Weight & Dimensions (Excel, mandatory) ─── */}
                        <SectionHeader title="Weight & Dimensions" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="serviceTypePrimary" label="Primary Service Type *" width="100%" options={serviceTypeOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="serviceTypeSecondary" label="Secondary Service Type" width="100%" options={serviceTypeOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="chargeableWeightRule" label="Chargeable Weight Rule *" width="100%" options={chargeableWeightRuleOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="minChargeableWeight" label="Min Chargeable Weight (kg) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxWeightSinglePiece" label="Max Weight — Single Piece (kg) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxWeightPerShipment" label="Max Weight — Per Shipment (kg) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="volumetricDivisor" label="Volumetric Divisor (cm³/kg) *" type="number" />
                        </Grid>
                        {/* Allocation weight band: max(dead, volumetric) kg must fall inside, else the courier is
                            skipped for the next one — same behaviour as the COD/Prepaid gate. Blank = open side. */}
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="effectiveMinWeight" label="Effective Min Weight (kg) — allocation" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="effectiveMaxWeight" label="Effective Max Weight (kg) — allocation" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxLength" label="Max Length (cm) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxWidth" label="Max Width (cm) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxHeight" label="Max Height (cm) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxGirth" label="Max Girth (cm) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxLwhSum" label="Max L+W+H Sum (cm) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="multiPieceAllowed" label="Multi-Piece Allowed *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxPiecesPerShipment" label="Max Pieces / Shipment *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="fragileHandlingAvailable" label="Fragile Handling *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="specialPackagingRequired" label="Special Packaging Required *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="remarks" label="Remarks *" />
                        </Grid>

                        {/* ─── Coverage & SLA (Excel, mandatory) ─── */}
                        <SectionHeader title="Coverage & SLA" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="metroServiceable" label="Metro Serviceable *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="nddEligible" label="NDD Eligible *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="sddEligible" label="SDD Eligible *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="forwardSlaMetroDays" label="Forward SLA — Metro (days) *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="forwardSlaNonMetroDays" label="Forward SLA — Non-Metro (days) *" type="number" />
                        </Grid>

                        {/* ─── Operations (Excel, mandatory) ─── */}
                        <SectionHeader title="Operations" />
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="pickupCutoffTime" label="Pickup Cutoff Time (HH:mm) *" placeholder="11:00" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="pickupDays" label="Pickup Days *" width="100%" options={workingDaysOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="maxDeliveryAttempts" label="Max Delivery Attempts *" type="number" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="deliveryDays" label="Delivery Days *" width="100%" options={workingDaysOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="ndrHandling" label="NDR Handling" width="100%" options={ndrHandlingOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="realTimeTracking" label="Real-Time Tracking *" width="100%" options={YES_NO} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFSelectField name="trackingType" label="Tracking Type *" width="100%" options={trackingTypeOptions} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="onboardedDate" label="Onboarded Date *" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>

                        {/* B2B Specific Settings */}
                        {supported_shipment === 'B2B' && (
                            <>
                                <SectionHeader title="B2B Settings" />
                                <Grid item xs={12} sm={6} md={4}>
                                    <RHFSelectField name='trackingOnly' label="Tracking Only" width="100%" options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <RHFSelectField name='prozoInternal' label="Prozo Internal" width="100%" options={[
                                        { label: 'True', value: 'true' },
                                        { label: 'False', value: 'false' },
                                    ]} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <RHFSelectField name='podIntegration' label="PoD Via" width="100%" options={[
                                        { label: 'API (courier sends)', value: 'API' },
                                        { label: 'Manual (we upload)', value: 'MANUAL' },
                                    ]} />
                                </Grid>
                            </>
                        )}

                    </Grid>
                </CardContent>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, pt: 2, borderTop: '1px solid #EEF2F6' }}>
                <Button variant="outlined" size="medium" onClick={() => reset()}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}>
                    Reset
                </Button>
                <LoadingButton loading={isLoading} variant="contained" size="medium" type="submit"
                    sx={{ textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}>
                    {isEditData ? 'Update Courier' : 'Add Courier'}
                </LoadingButton>
            </Box>
        </FormProvider>
    );
}
